import { getSession, saveSession, type PlayerSession } from "./session";

const LOCAL_ACCOUNTS_KEY = "guandan.nickname.accounts.local.v2";
const LEGACY_CREDENTIAL_PREFIX = "guandan.nickname.credential.v1:";
const LOGIN_DEVICE_ID_KEY = "guandan.login.device.v1";
let ephemeralLoginDeviceId = "";

export interface NicknameLoginResult {
  readonly session: PlayerSession;
  readonly firstLogin: boolean;
}

export interface NicknameStatusResult {
  readonly nickname: string;
  readonly registered: boolean;
}

interface LocalAccount {
  readonly userId: string;
  readonly passwordHash: string;
}

export async function getNicknameStatus(
  nicknameInput: string,
): Promise<NicknameStatusResult> {
  const nickname = normalizeNickname(nicknameInput);
  const key = normalizeNicknameKey(nickname);
  localStorage.removeItem(`${LEGACY_CREDENTIAL_PREFIX}${key}`);
  const endpoint = configuredEndpoint();
  if (!endpoint)
    return { nickname, registered: Boolean(readLocalAccounts()[key]) };
  const response = await fetch(`${endpoint}/auth/nickname/status`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ nickname, deviceId: getLoginDeviceId() }),
  });
  const payload = (await response.json().catch(() => null)) as {
    nickname?: unknown;
    registered?: unknown;
    error?: { message?: unknown };
  } | null;
  if (!response.ok)
    throw new Error(errorMessage(payload, "昵称检查失败，请重试"));
  if (
    typeof payload?.nickname !== "string" ||
    typeof payload.registered !== "boolean"
  )
    throw new Error("登录服务返回的昵称状态无效");
  return { nickname: payload.nickname, registered: payload.registered };
}

export async function loginWithNickname(
  nicknameInput: string,
  passwordInput: string,
): Promise<NicknameLoginResult> {
  const nickname = normalizeNickname(nicknameInput);
  const password = validatePassword(passwordInput);
  const key = normalizeNicknameKey(nickname);
  localStorage.removeItem(`${LEGACY_CREDENTIAL_PREFIX}${key}`);
  const endpoint = configuredEndpoint();
  if (!endpoint) return await localLogin(nickname, key, password);

  const response = await fetch(`${endpoint}/auth/nickname`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      nickname,
      password,
      deviceId: getLoginDeviceId(),
    }),
  });
  const payload = (await response.json().catch(() => null)) as {
    sessionToken?: unknown;
    session?: {
      userId?: unknown;
      email?: unknown;
      nickname?: unknown;
      expiresAt?: unknown;
    };
    firstLogin?: unknown;
    error?: { message?: unknown };
  } | null;
  if (!response.ok) throw new Error(errorMessage(payload, "昵称或密码不正确"));
  if (
    typeof payload?.sessionToken !== "string" ||
    typeof payload.session?.userId !== "string" ||
    typeof payload.session.email !== "string" ||
    typeof payload.session.nickname !== "string" ||
    typeof payload.session.expiresAt !== "number" ||
    typeof payload.firstLogin !== "boolean"
  )
    throw new Error("登录服务返回的玩家身份无效");
  const session: PlayerSession = {
    token: payload.sessionToken,
    userId: payload.session.userId,
    email: payload.session.email,
    nickname: payload.session.nickname,
    kind: "nickname",
    expiresAt: payload.session.expiresAt,
  };
  saveSession(session);
  return { session, firstLogin: payload.firstLogin };
}

export async function claimNicknamePassword(
  passwordInput: string,
): Promise<NicknameStatusResult> {
  const password = validatePassword(passwordInput);
  const session = getSession();
  if (!session) throw new Error("登录会话已失效");
  const nickname = normalizeNickname(session.nickname ?? "");
  const key = normalizeNicknameKey(nickname);
  const endpoint = configuredEndpoint();
  if (!endpoint) {
    const accounts = readLocalAccounts();
    const existing = accounts[key];
    const localUserId = session.userId.replace(/^local:/u, "");
    if (existing && existing.userId !== localUserId)
      throw new Error("该昵称已被注册");
    if (!existing) {
      accounts[key] = {
        userId: localUserId,
        passwordHash: await hashLocalPassword(password),
      };
      localStorage.setItem(LOCAL_ACCOUNTS_KEY, JSON.stringify(accounts));
    }
    return { nickname, registered: true };
  }
  const response = await fetch(`${endpoint}/auth/nickname/claim`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${session.token}`,
      "content-type": "application/json",
    },
    body: JSON.stringify({ password }),
  });
  const payload = (await response.json().catch(() => null)) as {
    nickname?: unknown;
    registered?: unknown;
    error?: { message?: unknown };
  } | null;
  if (!response.ok)
    throw new Error(errorMessage(payload, "密码设置失败，请重试"));
  if (
    typeof payload?.nickname !== "string" ||
    payload.registered !== true
  )
    throw new Error("登录服务返回的昵称状态无效");
  return { nickname: payload.nickname, registered: true };
}

function configuredEndpoint(): string | null {
  const configured = (import.meta.env.VITE_GAME_API_URL ??
    import.meta.env.VITE_AUTH_API_URL) as string | undefined;
  return configured?.trim() ? configured.replace(/\/$/u, "") : null;
}

function getLoginDeviceId(): string {
  if (ephemeralLoginDeviceId) return ephemeralLoginDeviceId;
  try {
    const stored = localStorage.getItem(LOGIN_DEVICE_ID_KEY)?.trim() ?? "";
    if (/^[A-Za-z0-9_-]{20,128}$/u.test(stored)) {
      ephemeralLoginDeviceId = stored;
      return stored;
    }
    ephemeralLoginDeviceId = crypto.randomUUID();
    localStorage.setItem(LOGIN_DEVICE_ID_KEY, ephemeralLoginDeviceId);
    return ephemeralLoginDeviceId;
  } catch {
    ephemeralLoginDeviceId ||= crypto.randomUUID();
    return ephemeralLoginDeviceId;
  }
}

async function localLogin(
  nickname: string,
  key: string,
  password: string,
): Promise<NicknameLoginResult> {
  const accounts = readLocalAccounts();
  const existing = accounts[key];
  if (existing) {
    if (!(await verifyLocalPassword(password, existing.passwordHash)))
      throw new Error("昵称或密码不正确");
    const session = localSession(existing.userId, nickname);
    saveSession(session);
    return { session, firstLogin: false };
  }
  const userId = crypto.randomUUID();
  accounts[key] = {
    userId,
    passwordHash: await hashLocalPassword(password),
  };
  localStorage.setItem(LOCAL_ACCOUNTS_KEY, JSON.stringify(accounts));
  const session = localSession(userId, nickname);
  saveSession(session);
  return { session, firstLogin: true };
}

function localSession(userId: string, nickname: string): PlayerSession {
  return {
    token: `local:${crypto.randomUUID()}`,
    userId: `local:${userId}`,
    email: `guest-${userId}@players.peterhub.invalid`,
    nickname,
    kind: "nickname",
    expiresAt: Date.now() + 365 * 24 * 60 * 60_000,
  };
}

function readLocalAccounts(): Record<string, LocalAccount> {
  try {
    const value: unknown = JSON.parse(
      localStorage.getItem(LOCAL_ACCOUNTS_KEY) ?? "{}",
    ) as unknown;
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    const accounts: Record<string, LocalAccount> = {};
    for (const [key, candidate] of Object.entries(value)) {
      if (
        !candidate ||
        typeof candidate !== "object" ||
        Array.isArray(candidate)
      )
        continue;
      const row = candidate as Readonly<Record<string, unknown>>;
      if (
        typeof row.userId === "string" &&
        typeof row.passwordHash === "string"
      )
        accounts[key] = {
          userId: row.userId,
          passwordHash: row.passwordHash,
        };
    }
    return accounts;
  } catch {
    return {};
  }
}

function normalizeNickname(value: string): string {
  const nickname = value.normalize("NFKC").trim();
  const length = Array.from(
    new Intl.Segmenter("zh-CN", { granularity: "grapheme" }).segment(nickname),
  ).length;
  if (length < 2 || length > 12) throw new Error("昵称须为 2—12 个字符");
  return nickname;
}

function normalizeNicknameKey(value: string): string {
  return value.normalize("NFKC").trim().toLowerCase();
}

function validatePassword(value: string): string {
  const length = Array.from(value).length;
  if (length < 8 || length > 64 || value.trim().length === 0)
    throw new Error("密码须为 8—64 个字符");
  return value;
}

async function hashLocalPassword(password: string): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const derived = await deriveLocalPassword(password, salt);
  return `pbkdf2-sha256$120000$${encodeBytes(salt)}$${encodeBytes(derived)}`;
}

async function verifyLocalPassword(
  password: string,
  encoded: string,
): Promise<boolean> {
  const [algorithm, iterations, saltText, expectedText] = encoded.split("$");
  if (
    algorithm !== "pbkdf2-sha256" ||
    iterations !== "120000" ||
    !saltText ||
    !expectedText
  )
    return false;
  try {
    const actual = await deriveLocalPassword(password, decodeBytes(saltText));
    const expected = decodeBytes(expectedText);
    if (actual.length !== expected.length) return false;
    let difference = 0;
    for (let index = 0; index < actual.length; index += 1)
      difference |= (actual[index] ?? 0) ^ (expected[index] ?? 0);
    return difference === 0;
  } catch {
    return false;
  }
}

async function deriveLocalPassword(
  password: string,
  salt: Uint8Array,
): Promise<Uint8Array> {
  const material = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(password),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const saltBuffer = Uint8Array.from(salt).buffer;
  const result = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      hash: "SHA-256",
      salt: saltBuffer,
      iterations: 120_000,
    },
    material,
    256,
  );
  return new Uint8Array(result);
}

function encodeBytes(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/u, "");
}

function decodeBytes(value: string): Uint8Array {
  const normalized = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
}

function errorMessage(
  payload: { error?: { message?: unknown } } | null,
  fallback: string,
): string {
  return typeof payload?.error?.message === "string"
    ? payload.error.message
    : fallback;
}
